import React, { useState, useEffect } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  IconButton,
  Paper,
  styled,
  keyframes,
} from '@mui/material';
import {
  Close,
  Remove,
  CheckCircle,
  RadioButtonChecked,
  AccessTime,
  Error as ErrorIcon,
  ExpandMore,
} from '@mui/icons-material';
import { SUPPORT_WIDGET_CLEARANCE } from '@shared/constants/layout';
import { formatDuration } from './formatDuration';
import { STALL_AFTER_MS, failureReasonText, uploadWaitText } from './runState';

const STALL_TEXT = 'Still working. Large files can take a few minutes to read.';

// Animations
const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Styled components
const DashboardContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  // Above the support chat bubble, which shares this corner.
  bottom: SUPPORT_WIDGET_CLEARANCE,
  right: 16,
  background: 'white',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  borderRadius: theme.spacing(2),
  minWidth: 500,
  maxWidth: 600,
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  zIndex: 9999,
  animation: `${slideIn} 0.3s ease-out`,
}));

const MinimizedBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  // Above the support chat bubble, which shares this corner.
  bottom: SUPPORT_WIDGET_CLEARANCE,
  right: 16,
  background: 'white',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  minWidth: 350,
  cursor: 'pointer',
  zIndex: 9999,
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
}));

const PulsingIcon = styled(RadioButtonChecked)(({ theme }) => ({
  color: theme.palette.primary.main,
  animation: `${pulse} 1.5s ease-in-out infinite`,
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '& .MuiLinearProgress-bar': {
    borderRadius: 5,
    transition: 'transform 0.4s ease',
  },
}));

const TaskItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'status',
})<{ isActive?: boolean; status?: Task['status'] }>(
  ({ theme, isActive, status }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    transition: 'all 0.3s ease',
    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
    opacity: status === 'COMPLETED' ? 0.55 : status === 'PENDING' ? 0.8 : 1,
  })
);

const SkeletonBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'skeleton-loading 1.5s ease-in-out infinite',
  borderRadius: theme.spacing(1),
  '@keyframes skeleton-loading': {
    '0%': {
      backgroundPosition: '200% 0',
    },
    '100%': {
      backgroundPosition: '-200% 0',
    },
  },
}));

interface Task {
  task_type: string;
  task_name: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress_percentage?: number;
  current_step?: string;
  execution_time?: number;
}

interface Props {
  documentId: string;
  documentType: string;
  tasks: Task[];
  overallProgress: number;
  estimatedTimeLeft: number;
  isGenerating: boolean;
  finalStatus?: 'COMPLETED' | 'FAILED' | 'CANCELLED' | null;
  error?: any;
  onClose: () => void;
  isLoading?: boolean;
  autoMinimize?: boolean;
  expandedSignal?: number;
  /**
   * True when a failed run is given back whatever it was charged, so the
   * failed popup can say so. Reports and demand letters are (their failure
   * handlers refund unless the document was already delivered, ZLAYT-1526);
   * deposition summaries were not checked (ZLAYT-1642).
   */
  chargeReturnedOnFailure?: boolean;
}

export const GenerationProgressDashboard: React.FC<Props> = ({
  documentType,
  tasks,
  overallProgress,
  estimatedTimeLeft,
  isGenerating,
  finalStatus,
  error,
  onClose,
  isLoading = false,
  autoMinimize = true,
  expandedSignal,
  chargeReturnedOnFailure = false,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const displayProgress = Math.min(
    100,
    Math.max(isGenerating ? 1 : 0, Math.round(overallProgress))
  );
  const isComplete =
    finalStatus === 'COMPLETED' ||
    displayProgress >= 100 ||
    (tasks.length > 0 && tasks.every((task) => task.status === 'COMPLETED'));
  const isFailed = finalStatus === 'FAILED' || (!!error && !isGenerating);
  const isCancelled = finalStatus === 'CANCELLED';
  const isFinished = isComplete || isFailed || isCancelled;
  // ZLAYT-1642: a step waiting on an upload read keeps its percent, which
  // looked stuck (Jack Zinda, 3 Nov 2026: 28% for four minutes with no
  // reason). The server's own words for the wait are under the step; the
  // percent and the time estimate make way for "Waiting" until it ends.
  const running = isGenerating && !isFinished;
  const uploadWait = running ? uploadWaitText(tasks) : null;
  // ZLAYT-1642: a percent that has not moved for a while, whatever the
  // engine. The timer restarts on every change of the percent and is
  // dropped when the run ends; the wait wording above wins when present.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    setStalled(false);
    if (!running) return undefined;
    const timer = setTimeout(() => setStalled(true), STALL_AFTER_MS);
    return () => clearTimeout(timer);
  }, [displayProgress, running]);
  const stallText = running && stalled && !uploadWait ? STALL_TEXT : null;
  // The plain reason the server gave, unless the failed step already shows
  // the same words on its own line.
  const reason = isFailed ? failureReasonText(error) : '';
  const failureReason =
    reason &&
    !tasks.some(
      (task) =>
        task.status === 'FAILED' &&
        String(task.current_step || '').trim() === reason
    )
      ? reason
      : '';
  const headerBackground = isComplete
    ? 'linear-gradient(135deg, #12B76A 0%, #0E9F6E 100%)'
    : isFailed || isCancelled
      ? 'linear-gradient(135deg, #E11D48 0%, #B91C1C 100%)'
      : 'linear-gradient(135deg, #2579bf 0%, #1e5a8e 100%)';

  useEffect(() => {
    if (!autoMinimize) {
      setIsMinimized(false);
    }
  }, [autoMinimize]);

  useEffect(() => {
    if (expandedSignal !== undefined) {
      setIsMinimized(false);
    }
  }, [expandedSignal]);

  // Auto-minimize after 4 seconds
  useEffect(() => {
    if (isGenerating && autoMinimize) {
      const timer = setTimeout(() => setIsMinimized(true), 4000);
      return () => clearTimeout(timer);
    }
  }, [isGenerating, autoMinimize]);

  const formatTimeRemaining = (seconds: number): string => {
    if (isCancelled) return 'Generation canceled';
    if (isFailed) return 'Generation failed';
    if (isComplete) return 'Completed';
    const mins = Math.ceil(seconds / 60);
    if (mins < 1) return 'Less than a minute';
    if (mins === 1) return 'About 1 minute';
    return `About ${mins} minutes`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'IN_PROGRESS':
        return <PulsingIcon />;
      case 'FAILED':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'PENDING':
      default:
        return <AccessTime sx={{ color: 'text.disabled' }} />;
    }
  };

  const getProgressColor = (
    progress: number
  ): 'primary' | 'success' | 'error' => {
    if (isFailed) return 'error';
    return progress >= 90 ? 'success' : 'primary';
  };

  // Loading skeleton screen
  if (isLoading) {
    return (
      <DashboardContainer>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <SkeletonBox sx={{ height: 24, width: '60%', mb: 1 }} />
        </Box>
        <Box sx={{ p: 3 }}>
          <SkeletonBox sx={{ height: 10, width: '100%', mb: 1 }} />
          <SkeletonBox sx={{ height: 14, width: '40%', mb: 3 }} />
          {[1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <SkeletonBox
                sx={{ width: 24, height: 24, borderRadius: '50%' }}
              />
              <SkeletonBox sx={{ flex: 1, height: 20 }} />
            </Box>
          ))}
        </Box>
      </DashboardContainer>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <MinimizedBar onClick={() => setIsMinimized(false)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isFailed ? (
            <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />
          ) : (
            <PulsingIcon fontSize="small" />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {isFailed
                ? `${documentType} failed`
                : isComplete
                  ? `${documentType} complete`
                  : uploadWait
                    ? `Generating ${documentType}... Waiting`
                    : `Generating ${documentType}... ${displayProgress}%`}
            </Typography>
            {!(isComplete || isFailed) && (
              <Typography variant="caption" color="text.secondary">
                {uploadWait
                  ? 'Waiting for a file to be read'
                  : (stallText ?? formatTimeRemaining(estimatedTimeLeft))}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(false);
            }}
          >
            <ExpandMore fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </MinimizedBar>
    );
  }

  // Full dashboard
  return (
    <DashboardContainer>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: headerBackground,
          color: 'white',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: 'white' }}>
            {isFailed
              ? `${documentType} Failed`
              : isComplete
                ? `${documentType} Complete`
                : `Generating ${documentType}`}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9, color: 'white' }}>
            {isFailed
              ? 'Something went wrong during generation'
              : isComplete
                ? 'Your document is ready'
                : 'Your document is being created'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={() => setIsMinimized(true)}
            sx={{
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 28,
              minWidth: 28,
              padding: 0,
            }}
          >
            <Remove fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Progress Section */}
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          gutterBottom
        >
          {isCancelled
            ? 'Generation canceled'
            : isFailed
              ? 'Generation failed'
              : isComplete
                ? 'Completed'
                : uploadWait
                  ? 'Overall Progress: Waiting'
                  : `Overall Progress: ${displayProgress}%`}
        </Typography>
        <ProgressBar
          variant="determinate"
          value={displayProgress}
          color={
            isComplete
              ? 'success'
              : isCancelled || isFailed
                ? 'error'
                : getProgressColor(displayProgress)
          }
        />
        {/* The heading above already names a finished state; repeating it
            here printed it twice (ZLAYT-1337). */}
        {!isFinished && (
          <Typography variant="caption" color="text.secondary">
            {uploadWait
              ? 'The bar moves again once the file has been read.'
              : (stallText ?? formatTimeRemaining(estimatedTimeLeft))}
          </Typography>
        )}
        {/* ZLAYT-1642: the reason the server gave, and what became of the
            charge. A failed run and a cancelled run are both given back
            whatever they were charged (the failure handler and the cancel
            both call refund_use_case_template_credit); a rerun of a
            finished report was charged nothing. */}
        {isFailed && (failureReason || chargeReturnedOnFailure) && (
          <Box sx={{ mt: 1.5 }} data-testid="failure-reason">
            {failureReason && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {failureReason}
              </Typography>
            )}
            {chargeReturnedOnFailure && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: failureReason ? 0.5 : 0 }}
              >
                Anything this run was charged has been given back. You can
                generate it again.
              </Typography>
            )}
          </Box>
        )}

        {/* Task List */}
        <Box sx={{ mt: 3 }}>
          {tasks.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Initializing generation tasks...
              </Typography>
            </Box>
          ) : (
            tasks.map((task) => {
              const isCompleted = task.status === 'COMPLETED';
              const isInProgress = task.status === 'IN_PROGRESS';
              const isFailed = task.status === 'FAILED';
              return (
                <TaskItem
                  key={task.task_type}
                  isActive={isInProgress}
                  status={task.status}
                >
                  {getStatusIcon(task.status)}

                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={isInProgress ? 600 : 400}
                        sx={{
                          textDecoration: isCompleted ? 'line-through' : 'none',
                          color: isFailed ? 'error.main' : 'inherit',
                        }}
                      >
                        {task.task_name}
                      </Typography>
                      {isInProgress && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.12)',
                            color: 'primary.main',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontWeight: 600,
                          }}
                        >
                          In progress
                        </Typography>
                      )}
                    </Box>

                    {task.current_step && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}
                      >
                        └─ {task.current_step}
                      </Typography>
                    )}

                    {isCompleted && task.execution_time && (
                      <Typography variant="caption" color="text.secondary">
                        ({formatDuration(task.execution_time)})
                      </Typography>
                    )}
                    {task.status === 'PENDING' && (
                      <Typography variant="caption" color="text.secondary">
                        Queued
                      </Typography>
                    )}
                  </Box>
                </TaskItem>
              );
            })
          )}
        </Box>

        {/* Helpful tip */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'info.50',
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: 'info.main',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            💡 Tip: You can minimize this window and continue working. We'll
            notify you when it's ready!
          </Typography>
        </Box>
      </Box>
    </DashboardContainer>
  );
};
